import React, { useState, useEffect } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Play, Clock, Terminal, Code, Calendar, CheckCircle, XCircle, Loader } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatInCST } from '../utils/timezone';

export default function ScriptsAndCrons() {
  const [scripts, setScripts] = useState([]);
  const [cronJobs, setCronJobs] = useState([]);
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scriptDialogOpen, setScriptDialogOpen] = useState(false);
  const [cronDialogOpen, setCronDialogOpen] = useState(false);
  const [scriptForm, setScriptForm] = useState({
    name: '',
    description: '',
    script_type: 'bash',
    script_content: '',
    timeout_seconds: 300
  });
  const [cronForm, setCronForm] = useState({
    script_id: '',
    schedule: '0 0 * * *'
  });

  const loadData = async () => {
    try {
      const [scriptsRes, cronRes, execRes] = await Promise.all([
        api.get('/scripts'),
        api.get('/cron-jobs'),
        api.get('/script-executions?limit=20')
      ]);
      setScripts(scriptsRes.data);
      setCronJobs(cronRes.data);
      setExecutions(execRes.data);
    } catch (error) {
      toast.error('Failed to load scripts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleScriptSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/scripts', scriptForm);
      toast.success('Script created successfully');
      setScriptDialogOpen(false);
      setScriptForm({ name: '', description: '', script_type: 'bash', script_content: '', timeout_seconds: 300 });
      loadData();
    } catch (error) {
      toast.error('Failed to create script');
    }
  };

  const handleCronSubmit = async (e) => {
    e.preventDefault();
    try {
      const script = scripts.find(s => s.id === cronForm.script_id);
      await api.post('/cron-jobs', {
        ...cronForm,
        script_name: script?.name || '',
        is_enabled: true
      });
      toast.success('Cron job created successfully');
      setCronDialogOpen(false);
      setCronForm({ script_id: '', schedule: '0 0 * * *' });
      loadData();
    } catch (error) {
      toast.error('Failed to create cron job');
    }
  };

  const handleExecuteScript = async (scriptId) => {
    try {
      await api.post(`/scripts/${scriptId}/execute`);
      toast.success('Script execution started');
      setTimeout(loadData, 2000);
    } catch (error) {
      toast.error('Failed to execute script');
    }
  };

  const handleToggleScript = async (scriptId, currentStatus) => {
    try {
      await api.put(`/scripts/${scriptId}`, { is_enabled: !currentStatus });
      toast.success('Script status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update script');
    }
  };

  const handleToggleCron = async (jobId, currentStatus) => {
    try {
      await api.put(`/cron-jobs/${jobId}`, { is_enabled: !currentStatus });
      toast.success('Cron job status updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update cron job');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      running: <Badge className="bg-blue-100 text-blue-800"><Loader className="h-3 w-3 mr-1 inline animate-spin" />Running</Badge>,
      completed: <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1 inline" />Completed</Badge>,
      failed: <Badge className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1 inline" />Failed</Badge>
    };
    return badges[status] || badges.running;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen">
        <img src="/cipherone-logo.png" alt="Loading" className="h-16 w-16 logo-spin mb-4" />
        <div className="text-lg text-gray-600">Loading automation...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="scripts-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold">Scripts & Automation</h1>
        <p className="text-gray-600 mt-1">Run scripts and schedule automated tasks</p>
      </div>

      <Tabs defaultValue="scripts" className="space-y-4">
        <TabsList>
          <TabsTrigger value="scripts">Scripts</TabsTrigger>
          <TabsTrigger value="cron">Cron Jobs</TabsTrigger>
          <TabsTrigger value="history">Execution History</TabsTrigger>
        </TabsList>

        <TabsContent value="scripts" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={scriptDialogOpen} onOpenChange={setScriptDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  New Script
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create New Script</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleScriptSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Script Name *</Label>
                      <Input value={scriptForm.name} onChange={(e) => setScriptForm({...scriptForm, name: e.target.value})} required placeholder="Daily Backup" />
                    </div>
                    <div>
                      <Label>Script Type</Label>
                      <Select value={scriptForm.script_type} onValueChange={(value) => setScriptForm({...scriptForm, script_type: value})}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="bash">Bash</SelectItem>
                          <SelectItem value="python">Python</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div>
                    <Label>Description</Label>
                    <Input value={scriptForm.description} onChange={(e) => setScriptForm({...scriptForm, description: e.target.value})} placeholder="What does this script do?" />
                  </div>
                  <div>
                    <Label>Script Content *</Label>
                    <Textarea value={scriptForm.script_content} onChange={(e) => setScriptForm({...scriptForm, script_content: e.target.value})} required rows={12} className="font-mono text-sm" placeholder="#!/bin/bash\necho 'Hello World'" />
                  </div>
                  <div>
                    <Label>Timeout (seconds)</Label>
                    <Input type="number" value={scriptForm.timeout_seconds} onChange={(e) => setScriptForm({...scriptForm, timeout_seconds: parseInt(e.target.value)})} min="10" max="3600" />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setScriptDialogOpen(false)}>Cancel</Button>
                    <Button type="submit">Create Script</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {scripts.length === 0 ? (
              <Card className="col-span-2">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <Terminal className="h-16 w-16 text-gray-400 mb-4" />
                  <p className="text-gray-600 mb-4">No scripts created yet</p>
                  <Button onClick={() => setScriptDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />Create Your First Script
                  </Button>
                </CardContent>
              </Card>
            ) : (
              scripts.map((script) => (
                <Card key={script.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <Terminal className="h-5 w-5" />
                          {script.name}
                        </CardTitle>
                        <CardDescription>{script.description}</CardDescription>
                      </div>
                      <Badge variant={script.is_enabled ? 'default' : 'secondary'}>
                        {script.is_enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="text-sm text-gray-600">
                      <span className="font-semibold">Type:</span> {script.script_type}
                    </div>
                    <div className="text-sm text-gray-600">
                      <span className="font-semibold">Timeout:</span> {script.timeout_seconds}s
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" onClick={() => handleExecuteScript(script.id)} disabled={!script.is_enabled}>
                        <Play className="h-3 w-3 mr-1" />Run Now
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleToggleScript(script.id, script.is_enabled)}>
                        {script.is_enabled ? 'Disable' : 'Enable'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        <TabsContent value="cron" className="space-y-4">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
            {scripts.length === 0 && (
              <p className="text-sm text-gray-500 italic">
                Create a script first before scheduling cron jobs
              </p>
            )}
            <div className="ml-auto">
              <Dialog open={cronDialogOpen} onOpenChange={setCronDialogOpen}>
                <DialogTrigger asChild>
                  <Button disabled={scripts.length === 0}>
                    <Plus className="h-4 w-4 mr-2" />Schedule Script
                  </Button>
                </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Schedule Cron Job</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCronSubmit} className="space-y-4">
                  <div>
                    <Label>Select Script *</Label>
                    <Select value={cronForm.script_id} onValueChange={(value) => setCronForm({...cronForm, script_id: value})} required>
                      <SelectTrigger><SelectValue placeholder="Choose a script" /></SelectTrigger>
                      <SelectContent>
                        {scripts.map((script) => (
                          <SelectItem key={script.id} value={script.id}>{script.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Cron Schedule *</Label>
                    <Select value={cronForm.schedule} onValueChange={(value) => setCronForm({...cronForm, schedule: value})}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="*/5 * * * *">Every 5 minutes</SelectItem>
                        <SelectItem value="0 * * * *">Every hour</SelectItem>
                        <SelectItem value="0 0 * * *">Daily at midnight</SelectItem>
                        <SelectItem value="0 0 * * 0">Weekly (Sunday)</SelectItem>
                        <SelectItem value="0 0 1 * *">Monthly (1st day)</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-gray-500 mt-1">Or enter custom: {cronForm.schedule}</p>
                  </div>
                  <div>
                    <Label>Custom Cron Expression</Label>
                    <Input value={cronForm.schedule} onChange={(e) => setCronForm({...cronForm, schedule: e.target.value})} placeholder="0 0 * * *" className="font-mono" />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="outline" onClick={() => setCronDialogOpen(false)}>Cancel</Button>
                    <Button type="submit">Schedule Job</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Scheduled Jobs</CardTitle>
            </CardHeader>
            <CardContent>
              {cronJobs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Clock className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  <p>No cron jobs scheduled</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Script</TableHead>
                      <TableHead>Schedule</TableHead>
                      <TableHead>Last Run</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {cronJobs.map((job) => (
                      <TableRow key={job.id}>
                        <TableCell className="font-medium">{job.script_name}</TableCell>
                        <TableCell className="font-mono text-sm">{job.schedule}</TableCell>
                        <TableCell>{job.last_run ? formatInCST(job.last_run, 'MMM d, h:mm a') : 'Never'}</TableCell>
                        <TableCell>
                          <Badge variant={job.is_enabled ? 'default' : 'secondary'}>
                            {job.is_enabled ? 'Active' : 'Disabled'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Button size="sm" variant="outline" onClick={() => handleToggleCron(job.id, job.is_enabled)}>
                            {job.is_enabled ? 'Disable' : 'Enable'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Execution History</CardTitle>
              <CardDescription>Recent script executions</CardDescription>
            </CardHeader>
            <CardContent>
              {executions.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Code className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  <p>No executions yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {executions.map((exec) => (
                    <Card key={exec.id} className="bg-gray-50">
                      <CardContent className="p-4">
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h4 className="font-semibold">{exec.script_name}</h4>
                            <p className="text-xs text-gray-500">
                              {formatInCST(exec.started_at, 'MMM d, yyyy h:mm:ss a')}
                              {exec.duration_seconds && ` • ${exec.duration_seconds.toFixed(2)}s`}
                            </p>
                          </div>
                          {getStatusBadge(exec.status)}
                        </div>
                        {exec.exit_code !== null && (
                          <div className="text-sm mb-2">
                            <span className="font-semibold">Exit Code:</span> <code className="bg-gray-200 px-2 py-1 rounded">{exec.exit_code}</code>
                          </div>
                        )}
                        {exec.stdout && (
                          <details className="text-sm">
                            <summary className="cursor-pointer font-semibold text-green-700">Output</summary>
                            <pre className="bg-gray-900 text-green-400 p-3 rounded mt-2 text-xs whitespace-pre-wrap break-all">{exec.stdout}</pre>
                          </details>
                        )}
                        {exec.stderr && (
                          <details className="text-sm mt-2">
                            <summary className="cursor-pointer font-semibold text-red-700">Errors</summary>
                            <pre className="bg-gray-900 text-red-400 p-3 rounded mt-2 text-xs whitespace-pre-wrap break-all">{exec.stderr}</pre>
                          </details>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
